<template>
    <div class="grid grid-cols-4 gap-16">
        <div class="col-span-3">
            <div tabindex="-1" ref="topOfScreen">
                <ui-notification
                    v-if="requiresComfirmation"
                    type="info"
                    :message="$t('multi-posting.campaigns.duplicate.info.message')"
                    class="mb-16"
                >
                </ui-notification>
                <p class="text-body font-semibold mb-8">{{ $t('multi-posting.campaigns.form.target_group.title') }}</p>
                <p>{{ $t('multi-posting.campaigns.form.target_group.description') }}</p>
            </div>
            <div class="grid grid-cols-2 gap-16">
                <div class="col-span-1">
                    <SelectDropdown
                        v-model="form.educationLevelId"
                        searchable
                        class="my-16"
                        :options="lookups.educationLevels"
                        :auto-translate="false"
                        :errors="form.errors.educationLevelId"
                        :disabled="onlySocialChannels"
                    >
                        {{ $t('multi-posting.campaigns.filters.education') + (!onlySocialChannels ? '*' : '') }}
                    </SelectDropdown>
                    <SelectDropdown
                        v-model="form.industryId"
                        searchable
                        class="my-16"
                        :options="lookups.industries"
                        :auto-translate="false"
                        :errors="form.errors.industryId"
                        :disabled="onlySocialChannels"
                    >
                        {{ $t('multi-posting.campaigns.filters.industry') + (!onlySocialChannels ? '*' : '') }}
                    </SelectDropdown>
                </div>
                <div class="col-span-1">
                    <SelectDropdown
                        v-model="form.jobFunctionId"
                        searchable
                        class="my-16"
                        :options="lookups.jobFunctions"
                        :auto-translate="false"
                        :errors="form.errors.jobFunctionId"
                        :disabled="onlySocialChannels"
                    >
                        {{ $t('multi-posting.campaigns.filters.job_function') + (!onlySocialChannels ? '*' : '') }}
                    </SelectDropdown>
                    <SelectDropdown
                        v-model="form.seniorityId"
                        searchable
                        class="my-16"
                        :options="lookups.seniority"
                        :auto-translate="false"
                        :errors="form.errors.seniorityId"
                        :disabled="onlySocialChannels"
                    >
                        {{ $t('multi-posting.campaigns.filters.seniority') + (!onlySocialChannels ? '*' : '') }}

                    </SelectDropdown>
                </div>
            </div>
            <div class="border-t w-full my-24"></div>
            <div>
                <p class="text-body font-semibold mb-8">{{ $t('multi-posting.campaigns.form.posting_details.title') }}</p>
                <p>{{ $t('multi-posting.campaigns.form.posting_details.description') }}</p>
            </div>
            <div class="grid grid-cols-2 gap-16">
                <div class="col-span-1">
                    <SelectDropdown
                        v-model="form.jobId"
                        searchable
                        class="my-16"
                        :options="mappedJobs"
                        :auto-translate="false"
                        :errors="form.errors.jobId"
                    >
                        {{ $t('multi-posting.campaigns.details.select_vacancy') + '*' }}
                    </SelectDropdown>
                    <SelectDropdown
                        v-model="form.region"
                        searchable
                        class="my-16"
                        :options="getCountriesSelect"
                        :auto-translate="false"
                        :errors="form.errors.country"
                    >
                        {{ $t('general.country') + '*' }}
                    </SelectDropdown>
                </div>
                <div class="col-span-1">
                    <DatePicker
                        v-model="form.startDate"
                        class="my-16 first-letter:uppercase"
                        disablePast
                        disableToday
                        disableWeekends
                        :errors="form.errors.startDate"
                        :input-label="$t('general.start_date') + '*'"
                    />
                    <SelectDropdown
                        v-model="form.publicationLanguage"
                        searchable
                        class="my-16"
                        :options="availableJobLanguages"
                        :auto-translate="false"
                        :errors="form.errors.publicationLanguage"
                    >
                        {{ $t('multi-posting.campaigns.details.publication_language') + '*' }}
                    </SelectDropdown>
                </div>
            </div>
            <div class="grid grid-cols-2 gap-16">
                <div class="col-span-2">
                    <div class="flex justify-between items-center">
                        <RangeSlider
                            v-model="form.yearsOfExperience"
                            :disabled="onlySocialChannels"
                            :min="0"
                            :max="40"
                            :step="1"
                            :offset="0"
                        >
                            <template #tooltip>
                                <p class="text-white">{{ form.yearsOfExperience + " " + $tc('general.years', form.yearsOfExperience)}}</p>
                            </template>
                            <template #before>
                                <p class="mr-8">0</p>
                            </template>
                            <template #after>
                                <p class="ml-8">40</p>
                            </template>
                            <span class="text-black font-semibold">{{ $t('multi-posting.campaigns.form.years_of_experience') }}</span>
                        </RangeSlider>
                    </div>
                </div>
                <div class="col-span-1">
                    <SelectDropdown
                        v-model="form.employmentType"
                        :disabled="onlySocialChannels"
                        :options="employmentTypes"
                        :auto-translate="false"
                        :errors="form.errors.employmentType"
                    >
                        {{ $t('multi-posting.campaigns.form.contract_type') + '*' }}
                    </SelectDropdown>
                </div>
                <div class="col-span-1">
                    <SimpleInput
                        v-model="form.weeklyWorkingHoursTo"
                        :disabled="onlySocialChannels"
                        type="number"
                        :min="1"
                        :errors="form.errors.weeklyWorkingHoursTo"
                        :input-label="$t('jobs.hours_per_week') + '*'"
                    />
                </div>
                <div class="col-span-2">
                    <div class="flex items-center w-fit hover:cursor-pointer" @click="showSalary = !showSalary">
                        <p class="font-semibold text-black select-none ">{{ $t('multi-posting.campaigns.form.salary_indication.title') }}</p>
                        <svg-icon
                            type="keyboard-arrow-down"
                            :class="{'rotate-180': showSalary}"
                            class="ml-8 transition-transform"
                        />
                    </div>
                </div>
                <div v-if="showSalary" class="col-span-2">
                    <div class="grid grid-cols-2 gap-16">
                        <div class="col-span-1">
                            <SimpleInput
                                v-model="form.salaryIndicationFrom"
                                :disabled="onlySocialChannels"
                                type="number"
                                :min="1"
                                :errors="form.errors.salaryIndicationFrom"
                                :input-label="$t('general.from')"
                            />
                        </div>
                        <div class="col-span-1">
                            <SimpleInput
                                v-model="form.salaryIndicationTo"
                                :disabled="onlySocialChannels"
                                type="number"
                                :min="1"
                                :errors="form.errors.salaryIndicationTo"
                                :input-label="$t('general.to')"
                            />
                        </div>
                        <div class="col-span-1">
                            <SelectDropdown
                                v-model="form.salaryIndicationCurrency"
                                :disabled="onlySocialChannels"
                                :options="currencies"
                                :auto-translate="false"
                                :errors="form.errors.salaryIndicationCurrency"
                            >
                                {{ $t('multi-posting.campaigns.form.salary_indication_currency') + '*' }}
                            </SelectDropdown>
                        </div>
                        <div class="col-span-1">
                            <SelectDropdown
                                v-model="form.salaryIndicationPeriod"
                                :disabled="onlySocialChannels"
                                :options="salaryIndicationPeriods"
                                :auto-translate="false"
                                :errors="form.errors.salaryIndicationPeriod"
                            >
                                {{ $t('multi-posting.campaigns.form.salary_indication_period') }}
                            </SelectDropdown>
                        </div>
                    </div>
                </div>

            </div>
            <div class="mt-16">
                <Wysiwyg
                    ref="description"
                    v-model="form.description"
                    full-featured
                    :errors="form.errors.description"
                >
                    {{ $t('general.description') + '*' }}
                </Wysiwyg>
            </div>
        </div>
        <div class="col-span-1">
            <slot name="basket"></slot>
        </div>
    </div>
</template>
<script>
import SelectDropdown from "../forms/SelectDropdown.vue";
import DatePicker from "../forms/DatePickerNew.vue";
import SimpleTextArea from "../forms/TextArea.vue";
import SimpleInput from "../forms/Input.vue";
import Wysiwyg from "../forms/Wysiwyg.vue";
import {mapActions, mapState} from "pinia";
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../../state/store';
import {useMultiPostingStore} from '../../state/modules/multiPosting';
import {useJobsStore} from '../../state/modules/jobs';
import Job from "../../models/Job";
import _ from "lodash";
import {getCountriesSelectOptions} from "../../../shared/helpers";
import RangeSlider from "../ui/RangeSlider.vue";
import UiNotification from "../ui/UiNotification.vue";
import SvgIcon from "../ui/SvgIcon.vue";

export default {
    name: 'CampaignFormDetails',
    components: {
        RangeSlider,
        Wysiwyg,
        SelectDropdown,
        SimpleTextArea,
        DatePicker,
        SimpleInput,
        UiNotification,
        SvgIcon
    },
    data() {
        return {
            showSalary: false,
        }
    },
    props: {
        direction: { type: String, default: "" },
        channelTypes: { type: Array, default: () => [] }
    },
    computed: {
        ...mapState(useMainStore, ['currentCompany', 'selectedLocale']),
        ...mapState(useMultiPostingStore, ['form', 'filters', 'lookups', 'selectedItems', 'requiresComfirmation']),
        getCountriesSelect() {
            return getCountriesSelectOptions(this.$i18n);
        },
        currencies() {
            return [
                { value: 'EUR', label: 'EUR' },
                { value: 'USD', label: 'USD' },
                { value: 'GBP', label: 'GBP' },
                { value: 'AUD', label: 'AUD' },
            ];
        },
        onlySocialChannels() {
            return this.selectedItems.filter(x => x.type !== 'socialChannel').length === 0;
        },
        locale() {
            return this.selectedLocale;
        },
        jobs() {
            return useRepo(Job)
                .where('published', true)
                .get();
        },
        mappedJobs() {
            return this.jobs
                .map(job => ({ value: job.id, label: job.title[this.locale] }));
        },
        publicationLanguages() {
            return this.currentCompany
                .jobsite
                .active_languages
                .map(lang => ({
                    value: lang,
                    label: _.capitalize(this.$t('languages.' + lang + '.adjective').toString())
                }));
        },
        availableJobLanguages() {
            if (!this.form.jobId) return [];
            const job = this.findJob();
            if (!job) return [];
            return this.publicationLanguages.filter(lang => !!job.active[lang.value]);
        },
        employmentTypes() {
            // employment types of VONQ // ref: https://vonq.stoplight.io/docs/hapi/2141b3a112cf5-employment-type
            return [
                { value: 'permanent', label: this.$t('multi-posting.options.employment-type.permanent') },
                { value: 'temporary', label: this.$t('multi-posting.options.employment-type.temporary') },
                { value: 'fixed_term', label: this.$t('multi-posting.options.employment-type.fixed_term') },
                { value: 'fixed_term_with_option_for_permanent', label: this.$t('multi-posting.options.employment-type.fixed_term_with_option_for_permanent') },
                { value: 'freelance', label: this.$t('multi-posting.options.employment-type.freelance') },
                { value: 'traineeship', label: this.$t('multi-posting.options.employment-type.traineeship') },
                { value: 'internship', label: this.$t('multi-posting.options.employment-type.internship') },
            ].sort((a, b) => a.label.localeCompare(b.label));
        },
        salaryIndicationPeriods() {
            return [
                { value: 'hourly', label: this.$t('multi-posting.options.salary-indication-period.hourly') },
                { value: 'monthly', label: this.$t('multi-posting.options.salary-indication-period.monthly') },
                { value: 'yearly', label: this.$t('multi-posting.options.salary-indication-period.yearly') },
            ].sort((a, b) => a.label.localeCompare(b.label));
        },
    },
    methods: {
        ...mapActions(useJobsStore, ['fetch']),
        ...mapActions(useMultiPostingStore, ['applyJobData', 'applyLocaleData', 'fetchTaxonomyEducationLevels',
            'fetchTaxonomySeniority', 'fetchTaxonomyJobTitles']),
        loadDetailsFromFilter() {
            if (!this.form.jobFunctionId) {
                this.form.jobFunctionId = this.filters.jobFunctionId;
            }
            if (!this.form.industryId) {
                this.form.industryId = this.filters.industryId;
            }
        },
        findJob() {
            return this.jobs.find(x => x.id === this.form.jobId);
        },
        handleJobData() {
            if (!this.jobs.length && !this.form.jobId) return;
            const job = this.findJob()
            this.applyJobData({job});
        },
        handleLocaleData() {
            if (!this.jobs.length && !this.form.jobId) return;
            const job = this.findJob()
            const locale = this.form.publicationLanguage
            this.applyLocaleData({job, locale})
            if (this.$refs.description) {
                this.$refs.description.setContent(this.form.description);
            }
        },
        initPublicationLanguage() {
            if (this.form.publicationLanguage) return

            if (this.publicationLanguages.includes(this.locale)) {
                this.form.publicationLanguage = this.locale;
            } else if (this.publicationLanguages.includes('en')) {
                this.form.publicationLanguage = 'en';
            } else {
                this.form.publicationLanguage = this.publicationLanguages[0].value;
            }
        },
    },
    watch: {
        'form.jobId'() {
            this.handleJobData();
            this.handleLocaleData();
        },
        'form.publicationLanguage'() {
            this.handleLocaleData();
        }
    },
    created() {
        this.initPublicationLanguage();
        this.fetch({activeState: 'active', all: 1});
    },
    beforeMount() {
        this.fetchTaxonomyEducationLevels();
        this.fetchTaxonomySeniority();
    },
    mounted() {
        if (this.direction === "forwards" && !this.onlySocialChannels) {
            this.loadDetailsFromFilter();
        }
        if (this.onlySocialChannels) {
            this.form.educationLevelId = null;
            this.form.industryId = null;
            this.form.jobFunctionId = null;
            this.form.seniorityId = null;
        }
        this.$nextTick(() => {
            this.$refs.topOfScreen.focus();
        })
    }
}
</script>
